// Partner program server functions.
//
// Our records are the source of truth for who a partner is, what they have
// referred, and what is owed. The payment platform is only the rail the
// money moves on, and it cannot see named referrals at all.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { isAdminClaims } from "@/lib/stripe.server";
import { emailDomainOf, normalizePartnerEmail } from "@/lib/partner-fee-guard";

export const NAMED_REFERRAL_WINDOW_DAYS = 180;
export const REDITUS_MARKETPLACE_HREF =
  "https://app.getreditus.com/marketplace/capitaldart";

function assertAdmin(claims: unknown) {
  if (!isAdminClaims(claims as Record<string, unknown>)) {
    throw new Error("Forbidden: admin only");
  }
}

export type PartnerRow = {
  id: string;
  email: string;
  contact_name: string;
  company_name: string | null;
  website: string | null;
  partner_type: string | null;
  audience: string | null;
  affiliate_slug: string | null;
  status: string;
  commission_pct: number;
  admin_note: string | null;
  applied_at: string;
  approved_at: string | null;
  user_id: string | null;
};

export type PartnerReferralRow = {
  id: string;
  company_name: string;
  contact_name: string | null;
  contact_email: string;
  submitted_at: string;
  expires_at: string;
  status: string;
  credited_at: string | null;
  first_purchase_at: string | null;
  purchased_amount_cents: number;
  commission_cents: number;
  notes: string | null;
};

const PARTNER_SELECT =
  "id, email, contact_name, company_name, website, partner_type, audience, affiliate_slug, status, commission_pct, admin_note, applied_at, approved_at, user_id";

const REFERRAL_SELECT =
  "id, company_name, contact_name, contact_email, submitted_at, expires_at, status, credited_at, first_purchase_at, purchased_amount_cents, commission_cents, notes";

/** First day of next month, which is when the monthly payment is made. */
export function nextPaymentDate(from: Date = new Date()): string {
  const next = new Date(
    Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 1),
  );
  return next.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------- apply

const applyInput = z.object({
  email: z.string().email().max(320),
  contactName: z.string().min(1).max(200),
  companyName: z.string().max(200).nullable().optional(),
  website: z.string().max(300).nullable().optional(),
  partnerType: z.string().max(100).nullable().optional(),
  audience: z.string().max(2000).nullable().optional(),
});

/** Self-serve application. Writes a partners record with status pending. */
export const applyToPartnerProgram = createServerFn({ method: "POST" })
  .inputValidator((data) => applyInput.parse(data))
  .handler(async ({ data }) => {
    const normalized = normalizePartnerEmail(data.email);
    if (!normalized) return { ok: false as const, reason: "invalid_email" };

    const { data: existing } = await supabaseAdmin
      .from("partners")
      .select("id, status")
      .eq("email_normalized", normalized)
      .maybeSingle();
    if (existing) {
      return {
        ok: true as const,
        duplicate: true as const,
        status: (existing as { status: string }).status,
      };
    }

    const { error } = await supabaseAdmin.from("partners").insert({
      email: data.email.trim(),
      email_normalized: normalized,
      email_domain: emailDomainOf(data.email),
      contact_name: data.contactName.trim(),
      company_name: data.companyName?.trim() || null,
      website: data.website?.trim() || null,
      partner_type: data.partnerType?.trim() || null,
      audience: data.audience?.trim() || null,
      status: "pending",
    });
    if (error) throw new Error(error.message);
    return { ok: true as const, duplicate: false as const, status: "pending" };
  });

// ------------------------------------------------------------- dashboard

export type PartnerDashboard = {
  partner: PartnerRow | null;
  referrals: PartnerReferralRow[];
  earnings: {
    earnedCents: number;
    pendingReferrals: number;
    signedUpReferrals: number;
    purchasedReferrals: number;
    nextPaymentDate: string;
  };
  referralLink: string | null;
};

/** The signed-in user's partner record, referrals and earnings. */
export const getMyPartnerDashboard = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<PartnerDashboard> => {
    const claims = context.claims as { email?: string } | null;
    const email = claims?.email ?? null;
    const normalized = normalizePartnerEmail(email);

    let partner: PartnerRow | null = null;
    const byUser = await supabaseAdmin
      .from("partners")
      .select(PARTNER_SELECT)
      .eq("user_id", context.userId)
      .maybeSingle();
    partner = (byUser.data as unknown as PartnerRow) ?? null;
    if (!partner && normalized) {
      const byEmail = await supabaseAdmin
        .from("partners")
        .select(PARTNER_SELECT)
        .eq("email_normalized", normalized)
        .maybeSingle();
      partner = (byEmail.data as unknown as PartnerRow) ?? null;
      // Bind the record to the account the partner actually signs in with.
      if (partner && !partner.user_id) {
        await supabaseAdmin
          .from("partners")
          .update({ user_id: context.userId, updated_at: new Date().toISOString() })
          .eq("id", partner.id);
      }
    }

    if (!partner) {
      return {
        partner: null,
        referrals: [],
        earnings: {
          earnedCents: 0,
          pendingReferrals: 0,
          signedUpReferrals: 0,
          purchasedReferrals: 0,
          nextPaymentDate: nextPaymentDate(),
        },
        referralLink: null,
      };
    }

    const { data: refData } = await supabaseAdmin
      .from("partner_referrals")
      .select(REFERRAL_SELECT)
      .eq("partner_id", partner.id)
      .order("submitted_at", { ascending: false });
    const referrals = (refData as unknown as PartnerReferralRow[]) ?? [];

    // Earnings cover both mechanisms: named referrals carry their own
    // totals, and link purchases land in the fee report log.
    const { data: feeData } = await supabaseAdmin
      .from("partner_fee_reports")
      .select("commission_cents, status, attribution_source")
      .eq("partner_id", partner.id);
    const linkEarned = ((feeData as unknown as Array<{
      commission_cents: number | null;
      status: string;
      attribution_source: string | null;
    }>) ?? [])
      .filter((r) => r.attribution_source === "link" && r.status !== "skipped_self_referral")
      .reduce((sum, r) => sum + (r.commission_cents ?? 0), 0);
    const namedEarned = referrals.reduce((sum, r) => sum + r.commission_cents, 0);

    return {
      partner,
      referrals,
      earnings: {
        earnedCents: linkEarned + namedEarned,
        pendingReferrals: referrals.filter((r) => r.status === "submitted").length,
        signedUpReferrals: referrals.filter((r) => r.status === "signed_up").length,
        purchasedReferrals: referrals.filter((r) => r.status === "purchased").length,
        nextPaymentDate: nextPaymentDate(),
      },
      referralLink: partner.affiliate_slug
        ? `https://capitaldart.com/?red=${encodeURIComponent(partner.affiliate_slug)}`
        : null,
    };
  });

// -------------------------------------------------------- named referral

const referralInput = z.object({
  companyName: z.string().min(1).max(200),
  contactName: z.string().max(200).nullable().optional(),
  contactEmail: z.string().email().max(320),
  notes: z.string().max(2000).nullable().optional(),
});

/** A partner names a company and contact they have spoken to. */
export const submitNamedReferral = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => referralInput.parse(data))
  .handler(async ({ context, data }) => {
    const claims = context.claims as { email?: string } | null;
    const partnerEmail = normalizePartnerEmail(claims?.email ?? null);
    const { data: pData } = await supabaseAdmin
      .from("partners")
      .select("id, email, status")
      .or(
        `user_id.eq.${context.userId}${partnerEmail ? `,email_normalized.eq.${partnerEmail}` : ""}`,
      )
      .maybeSingle();
    const partner = pData as { id: string; email: string; status: string } | null;
    if (!partner) return { ok: false as const, reason: "not_a_partner" };
    if (partner.status !== "approved") {
      return { ok: false as const, reason: "not_approved" };
    }

    const contactNormalized = normalizePartnerEmail(data.contactEmail);
    if (!contactNormalized) return { ok: false as const, reason: "invalid_email" };

    // A partner cannot name themselves.
    if (partnerEmail && contactNormalized === partnerEmail) {
      return { ok: false as const, reason: "self_referral" };
    }

    // Already named by someone, and still live: first submission wins.
    const { data: live } = await supabaseAdmin
      .from("partner_referrals")
      .select("id, partner_id")
      .eq("contact_email_normalized", contactNormalized)
      .gte("expires_at", new Date().toISOString())
      .maybeSingle();
    const liveRow = live as { id: string; partner_id: string } | null;
    if (liveRow && liveRow.partner_id !== partner.id) {
      return { ok: false as const, reason: "already_claimed" };
    }
    if (liveRow) return { ok: false as const, reason: "duplicate" };

    // The contact may already be a customer, in which case there is
    // nothing to refer.
    const { data: existingUser } = await supabaseAdmin
      .from("partner_signup_attributions")
      .select("id")
      .eq("email_normalized", contactNormalized)
      .maybeSingle();
    void existingUser;

    const { error } = await supabaseAdmin.from("partner_referrals").insert({
      partner_id: partner.id,
      partner_email: partner.email,
      company_name: data.companyName.trim(),
      contact_name: data.contactName?.trim() || null,
      contact_email: data.contactEmail.trim(),
      contact_email_normalized: contactNormalized,
      contact_email_domain: emailDomainOf(data.contactEmail),
      notes: data.notes?.trim() || null,
      status: "submitted",
    });
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

// ------------------------------------------------------------------ admin

export const listPartners = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<PartnerRow[]> => {
    assertAdmin(context.claims);
    const { data, error } = await supabaseAdmin
      .from("partners")
      .select(PARTNER_SELECT)
      .order("applied_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data as unknown as PartnerRow[]) ?? [];
  });

const decisionInput = z.object({
  id: z.string().uuid(),
  status: z.enum(["pending", "approved", "rejected", "suspended"]),
  affiliateSlug: z.string().max(120).nullable().optional(),
  commissionPct: z.number().int().min(1).max(100).nullable().optional(),
  adminNote: z.string().max(2000).nullable().optional(),
});

export const setPartnerStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => decisionInput.parse(data))
  .handler(async ({ context, data }) => {
    assertAdmin(context.claims);
    const patch: Record<string, unknown> = {
      status: data.status,
      updated_at: new Date().toISOString(),
    };
    if (data.affiliateSlug !== undefined) {
      patch.affiliate_slug = data.affiliateSlug?.trim() || null;
    }
    if (data.commissionPct != null) patch.commission_pct = data.commissionPct;
    if (data.adminNote !== undefined) patch.admin_note = data.adminNote?.trim() || null;
    if (data.status === "approved") {
      patch.approved_at = new Date().toISOString();
      patch.approved_by = context.userId;
    }
    const { error } = await supabaseAdmin
      .from("partners")
      .update(patch as never)
      .eq("id", data.id);
    if (error) {
      if (error.message.includes("partners_affiliate_slug_key")) {
        return { ok: false as const, reason: "slug_taken" };
      }
      throw new Error(error.message);
    }
    return { ok: true as const };
  });

export type AdminReferralRow = PartnerReferralRow & {
  partner_email: string;
  partner_id: string;
};

export const listAllNamedReferrals = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AdminReferralRow[]> => {
    assertAdmin(context.claims);
    const { data, error } = await supabaseAdmin
      .from("partner_referrals")
      .select(`${REFERRAL_SELECT}, partner_email, partner_id`)
      .order("submitted_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    return (data as unknown as AdminReferralRow[]) ?? [];
  });
